import { renderToStaticMarkup } from "react-dom/server";

import type { SldDocument } from "../services/sld";
import { layoutSld } from "../sld/layout";
import { draftFromDocument, type SldDraft } from "../sld/sldDraft";
import {
  buildSldSvg,
  resolveExportColours,
  sldExportFileName,
  titleBlockRows,
  type TitleBlockFacts,
} from "../sld/svgExport";
import { previewModelFromDraft, SldDiagram } from "./SldDiagram";

// Export of the single-line diagram (SLD-a, Master Prompt A26 (j)): a standalone SVG file
// and a print (the browser's print to PDF, A3 landscape through styles/sld-print.css). Both
// show the SAVED version only, so what leaves the product is traceable to a version and a
// content hash; while the draft on screen differs from it the panel says so. Nothing is
// exportable before the first save (version 0).

export const EXPORT_HINT = "Save the diagram to export it.";

export function unsavedChangesNotice(version: number): string {
  return `Export shows saved version ${version}; unsaved changes are not included.`;
}

type SldExportProps = {
  saved: SldDocument | null;
  draft: SldDraft;
  project: { code: string; name: string };
  revision: { revision_number: number; label: string; status: string };
};

/** A text file offered to the browser as a download. */
function downloadText(text: string, filename: string, type: string): void {
  if (typeof URL.createObjectURL !== "function") {
    return;
  }
  const url = URL.createObjectURL(new Blob([text], { type }));
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

export function SldExport({ saved, draft, project, revision }: SldExportProps) {
  const exportable = saved !== null && saved.version > 0;
  // The saved model through the same normaliser as the live preview, so the two compare.
  const savedModel = exportable ? previewModelFromDraft(draftFromDocument(saved)) : null;
  const changed =
    savedModel !== null &&
    JSON.stringify(previewModelFromDraft(draft)) !== JSON.stringify(savedModel);

  const facts = (): TitleBlockFacts | null =>
    saved === null || savedModel === null
      ? null
      : {
          projectCode: project.code,
          projectName: project.name,
          revisionLabel: revision.label,
          revisionStatus: revision.status,
          sldCode: savedModel.code,
          sldName: savedModel.name,
          version: saved.version,
          contentHash: saved.content_hash,
          updatedBy: saved.updated_by,
          updatedAt: saved.updated_at,
          generatedAt: new Date(),
        };

  function handleDownload(): void {
    const titleFacts = facts();
    if (saved === null || savedModel === null || titleFacts === null) {
      return;
    }
    // The picture exactly as the editor draws it, rendered apart from the page.
    const markup = renderToStaticMarkup(
      <SldDiagram model={savedModel} warnings={saved.check.warnings} />,
    );
    const picture = new DOMParser()
      .parseFromString(markup, "text/html")
      .querySelector('svg[role="img"]');
    if (picture === null) {
      return;
    }
    const layout = layoutSld(savedModel);
    const file = buildSldSvg({
      pictureSvg: new XMLSerializer().serializeToString(picture),
      pictureWidth: layout.width,
      pictureHeight: layout.height,
      facts: titleFacts,
      colours: resolveExportColours(document.documentElement),
    });
    downloadText(
      file,
      sldExportFileName(project.code, revision.revision_number, saved.version),
      "image/svg+xml",
    );
  }

  function handlePrint(): void {
    window.print();
  }

  const printFacts = facts();

  return (
    <section aria-label="Export" data-sld-export="true">
      <h3>Export</h3>
      <p data-sld-export-actions="true">
        <button type="button" disabled={!exportable} onClick={handleDownload}>
          Download SVG
        </button>{" "}
        <button type="button" disabled={!exportable} onClick={handlePrint}>
          Print / PDF
        </button>
      </p>
      {!exportable ? <p data-sld-export-hint="true">{EXPORT_HINT}</p> : null}
      {saved !== null && changed ? (
        <p role="status" data-sld-export-notice="true">
          {unsavedChangesNotice(saved.version)}
        </p>
      ) : null}
      {saved !== null && savedModel !== null && printFacts !== null ? (
        // The sheet: shown in print only (styles/sld-print.css), never read on screen.
        <div data-sld-print="true" aria-hidden="true">
          <table>
            <caption>Single-line diagram {savedModel.code}</caption>
            <tbody>
              {titleBlockRows(printFacts).map(([key, value]) => (
                <tr key={key}>
                  <th scope="row">{key}</th>
                  <td>{value}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <SldDiagram model={savedModel} warnings={saved.check.warnings} />
        </div>
      ) : null}
    </section>
  );
}

export default SldExport;
